import React, { useEffect, useState } from 'react';
import { Alert, AlertColor, Snackbar } from '@mui/material';
// イベントバスを使用してアプリケーション全体で通知を受け取るための実装
// カスタムイベントを定義
export const ACTION_NOTIFICATION_EVENT = 'action-notification';

// 通知データの型定義
export interface NotificationData {
  message: string;
  severity?: AlertColor;
  duration?: number;
}

// 通知イベントを発火する関数
export const notifyAction = (data: NotificationData): void => {
  // セッションストレージに保存（ページリロード後に表示するため）
  sessionStorage.setItem('action_notification', JSON.stringify(data));
  // イベントを発火
  const event = new CustomEvent(ACTION_NOTIFICATION_EVENT, { detail: data });
  window.dispatchEvent(event);
};
/**
 * アクション通知用の汎用コンポーネント
 * 操作成功・失敗時などに右下にSnackbarで通知を表示
 */
export const ActionNotifier: React.FC = () => {
  const [ open, setOpen ] = useState(false);
  const [ message, setMessage ] = useState('');
  const [ severity, setSeverity ] = useState<AlertColor>('success');
  const [ duration, setDuration ] = useState(5000);
  useEffect(() => {
    // イベントリスナーを登録
    const handleNotification = (event: Event) => {
      const customEvent = event as CustomEvent<NotificationData>;
      setMessage(customEvent.detail.message);
      setSeverity(customEvent.detail.severity || 'success');
      setDuration(customEvent.detail.duration || 5000);
      setOpen(true);
    };
    // イベントリスナーを登録
    window.addEventListener(ACTION_NOTIFICATION_EVENT, handleNotification);
    // セッションストレージから通知フラグをチェック (ページリロード後)
    const checkNotificationFlag = () => {
      const notification = sessionStorage.getItem('action_notification');
      if (notification) {
        try {
          const notificationData = JSON.parse(notification) as NotificationData;
          // 通知情報を表示
          setMessage(notificationData.message);
          setSeverity(notificationData.severity || 'success');
          setDuration(notificationData.duration || 5000);
          setOpen(true);
          // フラグを削除（一度だけ表示するため）
          sessionStorage.removeItem('action_notification');
        } catch (e) {
          // パース失敗時は何もしない
          console.error('Failed to parse notification data', e);
        }
      }
    };
    // ページロード時に通知フラグをチェック
    checkNotificationFlag();
    // クリーンアップ関数
    return () => {
      window.removeEventListener(ACTION_NOTIFICATION_EVENT, handleNotification);
    };
  }, []);
  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };
  return (
    <Snackbar
      open={open}
      autoHideDuration={duration}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      sx={{
        '& .MuiPaper-root': {
          minWidth: '300px',
          maxWidth: 'unset'  // 表示内容に合わせて幅が広がるように制限を解除
        }
      }}
    >
      <Alert
        onClose={handleClose}
        severity={severity}
        sx={{
          width: '100%',
          '& .MuiAlert-message': {
            fontSize: '1rem',  // フォントサイズを大きくする
            padding: '10px 0'  // 上下のパディングを増やして表示領域を広くする
          },
          '& .MuiAlert-icon': {
            fontSize: '1.5rem'  // アイコンサイズも大きくする
          }
        }}
      >
        {message}
      </Alert>
    </Snackbar>
  );
};
export default ActionNotifier;
